/** Report charts as plain SVG/CSS (no chart library): Wise Score scatter, posting heatmap, bars. */
import { plural } from '@vw/core/src/plural';
import { useMemo, useState } from 'react';
import { HEATMAP_DAYS, type ScatterPoint } from '@vw/core/src/report';
import { compact, date, mult } from '../format';

/** Wise Score scatter: posted date × views on a log scale, with the baseline and the Hot (3×) threshold. */
export function Scatter({ points, baseline, threshold, onPick }: {
  points: ScatterPoint[]; baseline: number | null; threshold: number | null; onPick: (pk: string) => void;
}) {
  const [hover, setHover] = useState<ScatterPoint | null>(null);
  const W = 720, H = 280, L = 52, R = 12, T = 12, B = 28;
  const geo = useMemo(() => {
    if (!points.length) return null;
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y).concat(baseline ? [baseline] : [], threshold ? [threshold] : []);
    const x0 = Math.min(...xs), x1 = Math.max(...xs) || x0 + 1;
    const y0 = Math.log10(Math.max(1, Math.min(...ys) * 0.8)), y1 = Math.log10(Math.max(...ys) * 1.2);
    const sx = (x: number) => L + ((x - x0) / Math.max(1, x1 - x0)) * (W - L - R);
    const sy = (y: number) => T + (1 - (Math.log10(Math.max(1, y)) - y0) / Math.max(0.001, y1 - y0)) * (H - T - B);
    const ticks: number[] = [];
    for (let e = Math.floor(y0); e <= Math.ceil(y1); e++) for (const m of [1, 3]) { const v = m * 10 ** e; if (Math.log10(v) >= y0 && Math.log10(v) <= y1) ticks.push(v); }
    return { sx, sy, ticks, x0, x1 };
  }, [points, baseline, threshold]);
  if (!geo) return <p className="text-sm text-muted-foreground">No dated reels in this window.</p>;
  const labelY = (y: number) => (y - 5 < T + 10 ? y + 14 : y - 5);
  return (
    <div className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Views of each reel over time, log scale, with the creator's usual views and the Hot (3×+) line">
        {geo.ticks.map((v) => (
          <g key={v}><line x1={L} x2={W - R} y1={geo.sy(v)} y2={geo.sy(v)} className="grid-line" /><text x={L - 6} y={geo.sy(v) + 4} className="axis" textAnchor="end">{compact(v)}</text></g>
        ))}
        <text x={L} y={H - 8} className="axis">{date(geo.x0)}</text>
        <text x={W - R} y={H - 8} className="axis" textAnchor="end">{date(geo.x1)}</text>
        {baseline && <line x1={L} x2={W - R} y1={geo.sy(baseline)} y2={geo.sy(baseline)} className="baseline" />}
        {threshold && <line x1={L} x2={W - R} y1={geo.sy(threshold)} y2={geo.sy(threshold)} className="threshold" />}
        {points.map((p) => (
          <circle key={p.pk} cx={geo.sx(p.x)} cy={geo.sy(p.y)} r={p.outlier ? 5 : 3.5}
            className={p.outlier ? 'dot hot' : p.collab ? 'dot collab' : 'dot'}
            onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)} onClick={() => onPick(p.pk)}>
            <title>{`${compact(p.y)} views · ${date(p.x)}`}</title>
          </circle>
        ))}
        {/* Line labels go last (on top of the dots) with a halo, at opposite ends so they never overlap, and below
            the line when it runs along the top edge (QA 2026-10-16: they were covered or clipped). */}
        {baseline && <text x={L + 6} y={labelY(geo.sy(baseline))} className="axis line-label" textAnchor="start">usual views</text>}
        {threshold && <text x={W - R - 2} y={labelY(geo.sy(threshold))} className="axis accent-text line-label" textAnchor="end">Hot line</text>}
      </svg>
      <div className="chart-note">{hover ? `${compact(hover.y)} views · ${date(hover.x)}${baseline ? ` · ${mult(hover.y / baseline)} usual` : ''}` : 'Each dot is a reel. Click one to open it.'}</div>
    </div>
  );
}

/** Day × hour heatmap. Values are counts or median Wise Scores (null = not enough reels). */
export function Heatmap({ cells, mode }: { cells: Array<Array<number | null>>; mode: 'count' | 'works' }) {
  const max = Math.max(1, ...cells.flat().map((v) => v ?? 0));
  return (
    <div className="heatmap" role="table" aria-label={mode === 'count' ? 'Reels posted per day and hour' : 'Median Wise Score per day and hour'}>
      <div className="hm-row hm-head" role="row"><span />{Array.from({ length: 24 }, (_, h) => <span key={h} className="axis">{h % 3 === 0 ? h : ''}</span>)}</div>
      {cells.map((row, d) => (
        <div className="hm-row" role="row" key={d}>
          <span className="axis">{HEATMAP_DAYS[d]}</span>
          {row.map((v, h) => {
            const level = v === null || v === 0 ? 0 : mode === 'count' ? v / max : Math.min(1, v / Math.max(3, max));
            const label = v === null ? 'not enough reels' : mode === 'count' ? plural(v, 'reel') : `median ${mult(v)}`;
            return <span key={h} role="cell" className="hm-cell" style={{ opacity: v === null || v === 0 ? 0.08 : 0.2 + level * 0.8 }} title={`${HEATMAP_DAYS[d]} ${h}:00 · ${label}`} />;
          })}
        </div>
      ))}
    </div>
  );
}

/** Simple labelled horizontal bars. */
export function Bars({ rows }: { rows: Array<{ label: string; value: number | null; note?: string }> }) {
  const max = Math.max(0.0001, ...rows.map((r) => r.value ?? 0));
  return (
    <div className="bars">
      {rows.map((r) => (
        <div className="bar-row" key={r.label}>
          <span className="bar-label">{r.label}</span>
          <span className="bar-track"><span className="bar-fill" style={{ width: `${((r.value ?? 0) / max) * 100}%` }} /></span>
          <span className="bar-value">{r.value === null ? '–' : mult(r.value)}{r.note ? <span className="muted small"> {r.note}</span> : null}</span>
        </div>
      ))}
    </div>
  );
}
